import React, { useEffect, useState, useCallback } from 'react';
import { Image as ImageIcon, Trash2, Crosshair } from 'lucide-react';
import { toast } from 'sonner';
import { api, formatApiError } from '../../lib/api';
import { focalPosition, FOCAL_CYCLE, FOCAL_LABELS } from '../../lib/photoFocal';
import { UpgradeNudge } from './shared';
import ImageEditor from '../../components/ImageEditor';

export function MediaTab({ memorialId, summary }) {
  const [media, setMedia] = useState([]);
  const [filter, setFilter] = useState('all');
  const [uploading, setUploading] = useState(false);
  // iter85 — Image editor wiring. Videos bypass the editor (no preview
  // canvas works on raw video frames); photos pass through it first.
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorFile, setEditorFile] = useState(null);
  const [editorVisibility, setEditorVisibility] = useState('public');

  const load = useCallback(() => {
    api.get(`/memorials/${memorialId}/media`).then(({ data }) => setMedia(data.media));
  }, [memorialId]);
  useEffect(() => { load(); }, [load]);

  const upload = async (file, visibility) => {
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('visibility', visibility);
    try {
      await api.post(`/memorials/${memorialId}/media`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      load();
    } catch (e) { alert(formatApiError(e)); } finally { setUploading(false); }
  };

  const updateVisibility = async (mid, v) => {
    await api.patch(`/media/${mid}`, { visibility: v });
    load();
  };
  const toggleSlideshow = async (mid, include) => {
    await api.patch(`/media/${mid}`, { include_in_slideshow: include });
    load();
  };
  const setFocal = async (mid, focal) => {
    try {
      await api.patch(`/media/${mid}`, { focal });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async (mid) => {
    if (!window.confirm('Are you sure you want to delete this photo?')) return;
    try {
      const { data } = await api.delete(`/media/${mid}`);
      if (data?.portrait_cleared) {
        toast.success('Photo deleted. It was the portrait — choose a new one from the Profile tab.');
      } else {
        toast.success('Photo deleted.');
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const filtered = filter === 'all' ? media : media.filter((m) => m.visibility === filter);

  return (
    <div className="space-y-5">
      {summary && <UpgradeNudge summary={summary} variant="inline" scope="media" />}
      <div className="ml-card">
        <h2 className="font-serif text-2xl text-[hsl(var(--primary))]">Upload memories</h2>
        <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">Choose where each photo or video belongs.</p>
        <div className="mt-4 grid sm:grid-cols-3 gap-3">
          {['public', 'vault', 'private'].map((v) => (
            <label
              key={v}
              className="rounded-md !p-4 cursor-pointer text-center border border-[hsl(var(--accent))] transition hover:brightness-95"
              style={{ backgroundColor: 'hsl(var(--accent))' }}
              data-testid={`upload-${v}`}
            >
              <div className="ml-overline !text-[hsl(var(--primary))] !font-bold">{v}</div>
              <div className="font-serif text-lg mt-1 text-[hsl(var(--primary))] font-bold capitalize">Upload to {v}</div>
              <input type="file" accept="image/*,video/*" className="hidden" onChange={(e) => {
                const f = e.target.files?.[0];
                if (!f) { e.target.value = ''; return; }
                // iter85 — photos go through the editor; videos upload raw.
                if (f.type && f.type.startsWith('image/')) {
                  setEditorFile(f);
                  setEditorVisibility(v);
                  setEditorOpen(true);
                } else {
                  upload(f, v);
                }
                e.target.value = '';
              }} />
            </label>
          ))}
        </div>
        {uploading && <div className="text-sm mt-3 text-[hsl(var(--muted-foreground))]">Uploading…</div>}
      </div>

      <div className="flex gap-2">
        {['all', 'public', 'vault', 'private'].map((v) => (
          <button key={v} onClick={() => setFilter(v)} data-testid={`filter-${v}`}
                  className={`px-4 py-1.5 text-xs uppercase tracking-widest rounded-full border font-bold transition ${
                    filter === v
                      ? 'bg-[hsl(var(--accent))] text-[hsl(var(--primary))] border-[hsl(var(--accent))]'
                      : 'border-border text-[hsl(var(--primary))] hover:bg-[hsl(var(--accent))]/15'
                  }`}>
            {v}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="ml-card text-center py-14">
          <ImageIcon className="w-10 h-10 mx-auto text-[hsl(var(--muted-foreground))]" />
          <p className="mt-4 text-sm text-[hsl(var(--muted-foreground))]">No memories yet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {filtered.map((m) => (
            <MediaThumb key={m.media_id} m={m} onUpdate={updateVisibility} onToggleSlideshow={toggleSlideshow} onFocal={setFocal} onDelete={del} />
          ))}
        </div>
      )}

      <ImageEditor
        open={editorOpen}
        file={editorFile}
        aspect={null}
        title="Adjust the photograph"
        saveLabel="Save & upload"
        onCancel={() => { setEditorOpen(false); setEditorFile(null); }}
        onSave={(edited) => {
          setEditorOpen(false);
          upload(edited, editorVisibility);
          setEditorFile(null);
        }}
      />
    </div>
  );
}

function MediaThumb({ m, onUpdate, onToggleSlideshow, onFocal, onDelete }) {
  const url = m.external_url || `${process.env.REACT_APP_BACKEND_URL}/api/media/${m.media_id}/file`;
  const isPhoto = m.kind !== 'video';
  const checked = !!m.include_in_slideshow;
  const focal = m.focal || 'auto';
  const cycleFocal = () => {
    const next = FOCAL_CYCLE[(FOCAL_CYCLE.indexOf(focal) + 1) % FOCAL_CYCLE.length];
    onFocal(m.media_id, next);
  };
  return (
    <div className="relative group rounded-xl overflow-hidden border bg-[hsl(var(--muted))] aspect-square">
      {m.kind === 'video' ? (
        <div className="w-full h-full grid place-items-center font-serif text-3xl text-[hsl(var(--primary))]">▶</div>
      ) : (
        <img src={url} alt={m.caption || ''} className="w-full h-full object-cover" style={{ objectPosition: focalPosition(m) }} />
      )}

      {/* Slideshow inclusion — photos only, shown in top-left. iter97.3 —
          Always visible on mobile (no hover); on desktop unchecked pill
          stays partially transparent until hover. */}
      {isPhoto && (
        <label
          className={`absolute top-2 left-2 flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] tracking-[0.14em] uppercase font-medium cursor-pointer transition-all backdrop-blur-sm ${
            checked
              ? 'bg-[hsl(var(--accent))]/95 text-[hsl(var(--primary))]'
              : 'bg-[hsl(var(--background))]/85 text-[hsl(var(--primary)/0.85)] sm:opacity-70 sm:group-hover:opacity-100'
          }`}
          data-testid={`slideshow-toggle-label-${m.media_id}`}
          onClick={(e) => e.stopPropagation()}
        >
          <input
            type="checkbox"
            className="w-3 h-3 accent-[hsl(var(--accent))]"
            checked={checked}
            onChange={(e) => onToggleSlideshow(m.media_id, e.target.checked)}
            data-testid={`slideshow-toggle-${m.media_id}`}
          />
          {checked ? 'In slideshow' : 'Add to slideshow'}
        </label>
      )}

      {/* iter105 — focal-point control. Cycles Auto → Top → Middle →
          Bottom so the admin decides which part of the photo the square
          thumbnail keeps (never touches the original file). */}
      {isPhoto && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); cycleFocal(); }}
          className="absolute top-10 left-2 flex items-center gap-1 px-2 py-1 rounded-full text-[10px] tracking-[0.12em] uppercase font-medium backdrop-blur-sm bg-[hsl(var(--background))]/85 text-[hsl(var(--primary)/0.85)] sm:opacity-70 sm:group-hover:opacity-100 transition-all hover:bg-[hsl(var(--background))]"
          data-testid={`focal-toggle-${m.media_id}`}
          title="Change which part of the photo the thumbnail shows"
        >
          <Crosshair className="w-3 h-3" /> {FOCAL_LABELS[focal]}
        </button>
      )}

      <div className="absolute inset-x-0 bottom-0 bg-[hsl(var(--primary))]/75 text-[#FDFBF7] p-2 text-[11px]">
        <select value={m.visibility} onChange={(e) => onUpdate(m.media_id, e.target.value)} className="bg-transparent border-[#FDFBF7]/30 border rounded px-1 py-0.5 text-[10px] uppercase w-full" data-testid={`visibility-${m.media_id}`}>
          <option value="public">Public</option>
          <option value="vault">Vault</option>
          <option value="private">Private</option>
        </select>
      </div>
      {/* iter97.3 — Delete button now ALWAYS visible (was hover-only,
          which made it unreachable on mobile/touch devices). 32px circular
          tap target keeps it accessible while staying tucked in the corner. */}
      <button
        onClick={() => onDelete(m.media_id)}
        className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/95 text-[hsl(var(--destructive))] shadow-md hover:bg-white hover:scale-105 active:scale-95 transition-all flex items-center justify-center"
        data-testid={`delete-${m.media_id}`}
        aria-label="Delete memory"
        title="Delete memory"
      >
        <Trash2 className="w-4 h-4" strokeWidth={2} />
      </button>
    </div>
  );
}
